// crdtMerge.js — zero-dependency CRDT merge engine pre local-first sync (TODUE V2)
// Adaptovaný PRINCÍP z cr-sqlite (MIT, frozen 2024): LWW-Element-Set + Hybrid Logical Clock.
// Deterministické, komutatívne, idempotentné zlúčenie offline zmien bez servera a bez straty dát.
// Zdroj: MasterProject/resources/lib/crdt-merge/crdt-merge.mjs (8/8 testov, Codex overený).
//
// Model: riadok = { id, fields: { <name>: {value, hlc} }, deleted: null | {hlc} }
// HLC = { ts: epoch-ms, count: int, node: deviceId } — rieši rozdielne hodiny zariadení.

// --- Hybrid Logical Clock ---------------------------------------------------

/** Deterministické porovnanie dvoch HLC. >0 ak a novšie, <0 ak b novšie, 0 ak zhodné. */
export function hlcCompare(a, b) {
  if (a.ts !== b.ts) return a.ts - b.ts;
  if (a.count !== b.count) return a.count - b.count;
  // tiebreak podľa deviceId — zaručuje deterministický výsledok pri identickom čase
  return a.node < b.node ? -1 : a.node > b.node ? 1 : 0;
}

/** Vytvor nový HLC pri lokálnej udalosti (posun oproti poslednému videnému). */
export function hlcNow(last, node, wallClock) {
  const now = wallClock ?? 0;
  if (last && last.ts >= now) return { ts: last.ts, count: last.count + 1, node };
  return { ts: now, count: 0, node };
}

// --- Merge -----------------------------------------------------------------

/** Bezpečné čítanie poľa podľa názvu — obíde accessor pre "__proto__" (JSON.parse ho robí
 *  own property, ale `fields[name]` by čítal prototype namiesto dát). */
function readField(fields, name) {
  if (!fields) return undefined;
  const d = Object.getOwnPropertyDescriptor(fields, name);
  return d ? d.value : undefined;
}

/** Vyber field zápis s vyšším HLC (LWW). Pri IDENTICKOM HLC tiebreak podľa hodnoty —
 *  zaručuje konvergenciu aj keby HLC omylom kolidoval (obrana proti pokazenému generovaniu). */
function pickField(a, b) {
  if (!a) return b;
  if (!b) return a;
  const c = hlcCompare(a.hlc, b.hlc);
  if (c !== 0) return c > 0 ? a : b;
  // HLC kolízia: deterministický tiebreak podľa serializovanej hodnoty (nie podľa poradia argumentov)
  return JSON.stringify(a.value) >= JSON.stringify(b.value) ? a : b;
}

/** Vyber tombstone s vyšším HLC (ak nejaký). */
function pickTombstone(a, b) {
  if (!a) return b ?? null;
  if (!b) return a;
  return hlcCompare(a.hlc, b.hlc) >= 0 ? a : b;
}

/**
 * Zlúč dva riadky s tým istým id. Komutatívne + idempotentné.
 * LWW-Element-Set: delete vyhráva len ak je NOVŠÍ než každá field zmena (edit po delete = resurrect).
 */
export function mergeRow(a, b) {
  if (!a) return b;
  if (!b) return a;

  // Object.create(null) — bez prototypu, aby názov poľa "__proto__" nezmenil prototype
  // a pole nezmizlo (Codex nález 2026-07-15: prototype pollution = silent data loss).
  const fields = Object.create(null);
  const names = new Set([...Object.keys(a.fields || {}), ...Object.keys(b.fields || {})]);
  for (const name of names) {
    // definuj vždy ako own data property (aj "__proto__") — priradenie fields[name] by inak
    // pre "__proto__" prepísalo prototype namiesto uloženia hodnoty
    Object.defineProperty(fields, name, {
      value: pickField(readField(a.fields, name), readField(b.fields, name)),
      enumerable: true, writable: true, configurable: true,
    });
  }

  const deleted = pickTombstone(a.deleted, b.deleted);
  return { id: a.id, fields, deleted };
}

/** Je riadok po merge živý? Delete-wins iba ak tombstone novší než najnovšia field zmena. */
export function isAlive(row) {
  if (!row.deleted) return true;
  let newestField = null;
  for (const f of Object.values(row.fields || {})) {
    if (!newestField || hlcCompare(f.hlc, newestField) > 0) newestField = f.hlc;
  }
  if (!newestField) return false; // len tombstone, žiadne pole → mŕtvy
  return hlcCompare(newestField, row.deleted.hlc) > 0; // edit po delete → resurrect
}

/**
 * Zlúč dve DB (mapy id→row). Vráti novú mapu — vstupy nemení.
 * Komutatívne: mergeDb(A,B) === mergeDb(B,A). Idempotentné: mergeDb(A, mergeDb(A,B)) === mergeDb(A,B).
 */
export function mergeDb(dbA, dbB) {
  const out = new Map();
  const ids = new Set([...dbA.keys(), ...dbB.keys()]);
  for (const id of ids) {
    out.set(id, mergeRow(dbA.get(id), dbB.get(id)));
  }
  return out;
}

/** Pomocník: vytvor field zápis. */
export function field(value, hlc) {
  return { value, hlc };
}

/**
 * Kanonická serializácia DB — sortí id AJ názvy polí, takže dve zariadenia s tým istým
 * OBSAHOM dostanú BAJTOVO ZHODNÝ reťazec bez ohľadu na poradie merge (Codex nález: iteračné
 * poradie inak závisí od poradia argumentov → rozdielny hash pri porovnaní `.db` cez Drive).
 */
export function canonical(db) {
  const ids = [...db.keys()].sort();
  return JSON.stringify(ids.map((id) => {
    const row = db.get(id);
    const names = Object.keys(row.fields).sort();
    return [id, names.map((n) => [n, row.fields[n]]), row.deleted, isAlive(row)];
  }));
}
